// The RFQ list's Health column: how far the price Odoo holds sits from the
// price the pricing engine would charge, per order.
//
// The backend sends raw sums only (`priceHealth` on each order of
// `GET /sale-orders`) and leaves the arithmetic here on purpose, so the page
// can change what it measures the drift against without a backend release.
// Kept out of the cell renderer so each rule is unit-testable on its own.

/** The block as the API sends it. Null when the order has no active lines at all. */
export interface PriceHealth {
  /** active lines with a manual or engine price in PE */
  engineLines: number;
  /** active lines with any price (> 0) in Odoo, the $1 placeholder included */
  odooLines: number;
  /** lines priced in both; the two amounts below cover exactly these */
  comparedLines: number;
  /** sum of (manualPrice ?? enginePrice) x qty over the compared lines */
  engineAmount: number;
  /** sum of Odoo unitPrice x qty over the compared lines */
  odooAmount: number;
  /** sum of Odoo unitPrice x qty over ALL active lines, freight included */
  odooTotal: number;
}

export type PriceHealthState =
  /** the drift is measured and shown */
  | 'measured'
  /** the order has lines, but none of them is priced on both sides */
  | 'nothing-to-compare'
  /** lines are compared, but our own side of them sums to zero */
  | 'no-benchmark'
  /** the order has no active line to measure: the API sent no block at all */
  | 'no-lines';

export interface PriceHealthReading {
  state: PriceHealthState;
  /** `odooAmount - engineAmount`. Positive means Odoo holds MORE than PE would charge. */
  driftAmount: number | null;
  /** The same drift as a share of the engine amount, in percent, same sign. */
  driftPercent: number | null;
  /** Which way the arrow points, derived from the ROUNDED percent so it never contradicts the number. */
  direction: 'up' | 'down' | 'level' | null;
  /** Priced in Odoo but not in PE: lines the drift above does not account for. */
  odooOnlyLines: number;
  /** Priced in PE but not in Odoo: the same gap on our own side. */
  engineOnlyLines: number;
  raw: PriceHealth | null;
}

/**
 * The priced lines the comparison leaves out, per side. The compared set is the
 * intersection of the two, so an order whose counts read "9 compared / PE 11 /
 * Odoo 10" is measured over 9 lines and silent about 3.
 *
 * Both sides count, because both leave the cell saying less than it appears to:
 * a line Odoo prices and we do not is money the drift never looked at, and a
 * line we price and Odoo does not is our own number missing from the quote.
 * Clamped at zero: `comparedLines` is the intersection and cannot exceed
 * either side, but the cell must not print a negative count if that ever
 * changes.
 */
const uncoveredLines = (priceHealth: PriceHealth) => ({
  odooOnlyLines: Math.max(0, priceHealth.odooLines - priceHealth.comparedLines),
  engineOnlyLines: Math.max(0, priceHealth.engineLines - priceHealth.comparedLines),
});

/** Decimals the column and the popover both show. The arrow follows this rounding. */
export const PERCENT_DECIMALS = 1;

const roundPercent = (value: number): number => Number(value.toFixed(PERCENT_DECIMALS));

/**
 * Turn the API block into everything the column needs.
 *
 * The percent is the drift over the ENGINE amount: the engine price is the
 * benchmark, so "+4.2%" reads as "Odoo is charging 4.2% more than we would".
 * The denominator is the compared lines only, never `odooTotal` — an order
 * with two of eleven lines compared would otherwise report a reassuring 1%
 * that is really a 30% drift on the part we can actually see.
 *
 * The denominator is the ABSOLUTE engine amount, because credit lines make
 * `engineAmount` negative on real orders (S20329 on the local DB sits at
 * -$287,425.28). Dividing by a negative benchmark flips the sign, and the cell
 * would then point a red arrow down at an order Odoo is charging $516,008 MORE
 * for. The scale is how far apart the two sides are; the direction belongs to
 * the money difference alone.
 */
export const readPriceHealth = (priceHealth?: PriceHealth | null): PriceHealthReading => {
  if (!priceHealth) {
    return {
      state: 'no-lines',
      driftAmount: null,
      driftPercent: null,
      direction: null,
      odooOnlyLines: 0,
      engineOnlyLines: 0,
      raw: null,
    };
  }

  const { comparedLines, engineAmount, odooAmount } = priceHealth;

  if (comparedLines === 0) {
    return {
      state: 'nothing-to-compare',
      driftAmount: null,
      driftPercent: null,
      direction: null,
      ...uncoveredLines(priceHealth),
      raw: priceHealth,
    };
  }

  // Lines ARE compared, but our side of them comes to zero: a credit line
  // cancelling the lines beside it, or every compared line priced at 0. There
  // is a money difference to show, and no benchmark to express it as a share
  // of. Its own state, because the panel says why the cell is blank and
  // "no line is priced on both sides" would contradict the line counts sitting
  // directly above it.
  if (engineAmount === 0) {
    return {
      state: 'no-benchmark',
      driftAmount: Number((odooAmount - engineAmount).toFixed(2)),
      driftPercent: null,
      direction: null,
      ...uncoveredLines(priceHealth),
      raw: priceHealth,
    };
  }

  const driftAmount = Number((odooAmount - engineAmount).toFixed(2));
  const driftPercent = roundPercent((driftAmount / Math.abs(engineAmount)) * 100);

  return {
    state: 'measured',
    driftAmount,
    driftPercent,
    // Off the rounded value: a drift that displays as 0.0% must not carry an arrow.
    direction: driftPercent > 0 ? 'up' : driftPercent < 0 ? 'down' : 'level',
    ...uncoveredLines(priceHealth),
    raw: priceHealth,
  };
};

/**
 * The value the Health column sorts on. Null for every order with no drift to
 * show, so the grid can keep those together at one end.
 */
export const priceHealthSortValue = (priceHealth?: PriceHealth | null): number | null =>
  readPriceHealth(priceHealth).driftPercent;

/**
 * Sort comparator for the column. Orders with nothing to show sink to the
 * bottom in BOTH directions — they are not "the best" or "the worst", they are
 * simply not in the race, and AG Grid's default comparator would float them to
 * the top of an ascending sort.
 */
export const comparePriceHealth = (
  a: number | null | undefined,
  b: number | null | undefined,
  nodeA?: unknown,
  nodeB?: unknown,
  isDescending = false,
): number => {
  const aMissing = a === null || a === undefined;
  const bMissing = b === null || b === undefined;
  if (aMissing && bMissing) return 0;
  // The grid flips the result when sorting descending, so pre-flip it to keep
  // these rows pinned at the bottom either way.
  if (aMissing) return isDescending ? -1 : 1;
  if (bMissing) return isDescending ? 1 : -1;
  return a - b;
};
